/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2022, Zumper
 * @description getSearchUrlFromPathAndFilters
 */

import { PropertyFeatures } from '@zcode/enums'
import type { Filters } from '@zcode/state-type'
import { isNumber } from '@zcode/utils'
import {
  DEFAULT_SORT,
  FILTER_TYPES,
  PRICE_SORT_ASC_OPTION,
  PRICE_SORT_DESC_OPTION,
  URL_FILTER_QUERY_KEYS,
} from '../../../../shared/constants/filter'
import { updateUrlParam } from '../../../../shared/utils/urls/updateUrlParam'
import { eq, oisEmpty, omap } from '../../../../shared/utils/utils'

const filterFeatureVerticals = [
  PropertyFeatures.INCOME_RESTRICTED,
  PropertyFeatures.NO_SECURITY_DEPOSIT,
  PropertyFeatures.SPECIALS,
  PropertyFeatures.UTILITIES_INCLUDED,
] as const

const isNonEmptyArray = <T>(something?: readonly T[] | null): something is readonly T[] => {
  return !!(something as readonly T[] | undefined)?.length
}

const getFeatureParams = (features: readonly number[] | null) => {
  const featureParams = features?.filter((feature: number) => {
    return !filterFeatureVerticals.includes(feature)
  })
  return isNonEmptyArray(featureParams) ? featureParams.join(',') : ''
}

export const getSearchUrlFromPathAndFilters = ({
  filters,
  isFavoritesView,
  locationUrlQueries,
  omitBox,
  path,
}: {
  filters: Filters
  isFavoritesView?: boolean
  locationUrlQueries?: readonly string[]
  omitBox?: boolean
  path?: string
}) => {
  let url = path ?? ''
  url = updateUrlParam(url, 'favorites', isFavoritesView ? true : null)

  if (filters.orAmenities?.length) {
    url = updateUrlParam(url, 'or-amenities', JSON.stringify(filters.orAmenities))
  }

  if (filters.orFeatures?.length) {
    url = updateUrlParam(url, 'or-features', JSON.stringify(filters.orFeatures))
  }

  // bathroom
  url = updateUrlParam(url, 'bathrooms', filters.minBathrooms ?? null)
  if (isNonEmptyArray(filters.bathroomsRange)) {
    url = updateUrlParam(url, 'bathrooms-range', filters.bathroomsRange.join(','))
  }
  // propertyTypes
  if (isNonEmptyArray(filters.propertyTypes)) {
    url = updateUrlParam(url, 'property-types', filters.propertyTypes.join(','))
  }
  // guests
  if (isNonEmptyArray(filters.guests)) {
    url = updateUrlParam(url, 'guests', filters.guests.join(','))
  }
  // startDate & endDate
  url = updateUrlParam(
    url,
    URL_FILTER_QUERY_KEYS[FILTER_TYPES.START_DATE],
    filters.startDate ?? null
  )
  url = updateUrlParam(url, URL_FILTER_QUERY_KEYS[FILTER_TYPES.END_DATE], filters.endDate ?? null)
  url = updateUrlParam(
    url,
    URL_FILTER_QUERY_KEYS[FILTER_TYPES.MIN_ACTIVE_UNITS],
    filters.minActiveUnits ?? null
  )
  // sort, only update the url if sort is not the default sort or null
  if (isNonEmptyArray(filters.sort) && !eq(filters.sort, DEFAULT_SORT)) {
    const filtered = filters.sort.filter(
      (key) => key !== PRICE_SORT_ASC_OPTION && key !== PRICE_SORT_DESC_OPTION
    )
    const hasSort = filtered.length
    if (hasSort) {
      url = updateUrlParam(url, 'sort', filtered ? filtered.join(',') : null)
    } else {
      url = updateUrlParam(url, 'sort', null)
    }
  } else {
    url = updateUrlParam(url, 'sort', null)
  }
  // keywords
  url = updateUrlParam(url, 'keywords', filters.keywords ? filters.keywords : null)
  // price-per-bedroom
  url = updateUrlParam(
    url,
    'price-per-bedroom',
    filters.maxPricePerBedroom != null && Number(filters.maxPricePerBedroom) > 0
      ? filters.maxPricePerBedroom
      : null
  )
  // max hours
  url = updateUrlParam(url, 'max-days', isNumber(filters.maxDays) ? filters.maxDays : null)

  // transits
  url = updateUrlParam(
    url,
    'transits',
    !oisEmpty(filters.transits)
      ? omap(filters.transits, (t: string, d: string) => `${t}=${d || ''}`).join(',')
      : null
  )

  // lease-term
  if (filters.shortTerm) {
    url = updateUrlParam(url, 'lease-term', 'short')
  } else {
    url = updateUrlParam(url, 'lease-term', null)
  }

  // sqft
  const minSqft = isNumber(filters.minSquareFeet) ? filters.minSquareFeet : ''
  const maxSqft = isNumber(filters.maxSquareFeet) ? filters.maxSquareFeet : ''

  url = updateUrlParam(url, 'min-square-feet', minSqft || null)
  url = updateUrlParam(url, 'max-square-feet', maxSqft || null)

  // section8
  url = updateUrlParam(url, 'section8', filters.section8 ? true : null)

  // encoded-filters
  url = updateUrlParam(url, 'encoded-filters', filters.encodedFilters ?? null)

  // listing amenities
  if (isNonEmptyArray(filters.listingAmenities)) {
    url = updateUrlParam(url, 'listing-amenities', filters.listingAmenities.join(','))
  }

  // building amenities
  if (isNonEmptyArray(filters.buildingAmenities)) {
    url = updateUrlParam(url, 'building-amenities', filters.buildingAmenities.join(','))
  }

  // features
  if (isNonEmptyArray(filters.features)) {
    // Ignoring income restricted feature since that is the low-income vertical
    const features = filters.features.filter((feature: number) => {
      return !filterFeatureVerticals.includes(feature)
    })
    if (features.length) {
      url = updateUrlParam(url, 'features', features.join(','))
    }
  }

  // put back box
  if (!omitBox && locationUrlQueries && locationUrlQueries.length) {
    url += `${url.includes('?') ? '&' : '?'}${locationUrlQueries.join('&')}`
  }

  // images
  url = updateUrlParam(url, 'images', filters.hasImages ? true : null)

  // student housing
  url = updateUrlParam(url, 'student-housing', filters.studentHousing ? true : null)

  // age restricted
  url = updateUrlParam(url, 'age-restricted', filters.ageRestricted ? true : null)

  // live online tours
  url = updateUrlParam(url, 'live-online-tours', filters.liveOnlineTours ? true : null)

  // virtual tours
  url = updateUrlParam(url, 'has-virtual-tours', filters.hasVirtualTours ? true : null)

  // unavailable listings
  url = updateUrlParam(url, 'hide-no-vacancy', filters.hideNoVacancy ? true : null)

  const features = isNonEmptyArray(filters.features) ? filters.features : null
  const featureParams = getFeatureParams(features)
  if (featureParams?.length) {
    url = updateUrlParam(url, 'features', featureParams)
  }

  // qs (multi-queries)
  url = updateUrlParam(url, 'qs', filters.qs ?? null)

  // floorplans
  url = updateUrlParam(url, 'floorplans', filters.floorplans)

  // neighborhoodIds
  const hasNeighborhoodIds = !!(filters.neighborhoodIds && isNonEmptyArray(filters.neighborhoodIds))
  url = updateUrlParam(
    url,
    'neighborhood-ids',
    hasNeighborhoodIds ? filters.neighborhoodIds.join(',') : null
  )

  return url
}
